import { Box, Card, CardHeader, CardContent, useTheme, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { merge } from "lodash";
import { useGetList } from "ra-core";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LineChartWithPairsResponseDto, TimeRangeType } from "@/gen/api/metrics";
import { ChartLegends, generalizeData, genericChartOptions, LineChartDataEx, perfThreshold, useChartUtilsWithUserTime } from "../databases/metricsChartCommon";
import Chart from 'react-apexcharts'
import { RecPatch } from "@/dataProvider/tools";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import { tbgSxProps } from "../overrides/toggleButtonGroupSx";


const colorsOverride = [
  '#D32F2F',
  '#8E24AA',
  '#5C6BC0',
  '#26C6DA',
  '#388E3C',
  '#FFEB3B',
  '#F57C00',
  '#795548',
]

const secondsFormatter = (value:number) => { 
  const value1 = value
  const surfix = value1 >= 1000 ? 's' : 'ms'
  const num = value >= 1000 ? value / 1000 : value
  return `${Number.isInteger(num) ? num : num.toFixed(1)}${surfix}` 
}

const MaxQueryTimeChart: FC<{timeRange:TimeRangeType, endTime:string, setTimeRange: (tr:TimeRangeType) => void}> = ({
  timeRange,
  setTimeRange,
  endTime,
}) => {
  const theme = useTheme();
  const { pairWithUserTime } = useChartUtilsWithUserTime()

  const { data } = useGetList<LineChartWithPairsResponseDto & RecPatch>('dbmetrics-averagequerytime', pages(), sorts(), { timeRangeType: timeRange, endTime })
  const averageQueryTimeData: LineChartWithPairsResponseDto & RecPatch | undefined = Object.values(data || {})[0]

  const finalData = useMemo(() => {
    const inputData: LineChartWithPairsResponseDto = {
      legend: averageQueryTimeData?.legend,
      series: averageQueryTimeData?.series && averageQueryTimeData.series.map(s => ({
        name: s.name,
        data: s.data && s.data.map(pairWithUserTime)
      }))
    };

    return generalizeData(theme, inputData, colorsOverride)
  }, [theme, averageQueryTimeData])

  const [selectedSeries, setSelectedSeries] = useState(finalData.legend);
  const selectedSeriesRef = useRef(selectedSeries)
  useEffect(() => {
    setSelectedSeries(finalData.legend)
  }, [finalData])
  
  useEffect(() => {
    selectedSeriesRef.current = selectedSeries
  }, [selectedSeries])
  const handleChange = useCallback((x: LineChartDataEx, checked: boolean) => {
    if (!checked) {
      setSelectedSeries(selectedSeriesRef.current.filter((item) => item !== x.name));
    } else {
      setSelectedSeries([...selectedSeriesRef.current, x.name]);
    }
  }, []);

  const visibleSeries = useMemo(() => finalData.series.filter((item) => item.name && selectedSeries.includes(item.name)), [finalData, selectedSeries]);

  const genericOptions = useMemo(() => genericChartOptions(theme), [theme])

  const chart = useMemo(() => {
    const datapointCount = visibleSeries.map(s => s.data.length).reduce((acc, n) => acc + n, 0)

    const erase = {
      options: {
        ...(datapointCount > perfThreshold ? { markers: null } : {}),
      },
    }
    const patch = {
      options: {
        ...(datapointCount > 0 ? { 
          markers: { size: 0 },
        } : {}),
      },
    }
    return merge({}, genericOptions, {
      options: {
        colors: visibleSeries.map((item) => item.color),
        tooltip: {
          x: { format: 'dd MMM / HH:mm' }
        },
        xaxis: {
          type: 'datetime',
        },
        yaxis: [{
          // logarithmic: true,
          labels: {
            formatter: secondsFormatter,
          },
        }],
      },
      series: visibleSeries.map(s => ({
        ...s,
        data: s.data
      })),
    }, erase, patch)
  }, [genericOptions, visibleSeries, finalData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
      }}
    >
      <Card>
        <CardHeader 
          title="Average Query Processing Time" 
          action={
            <ToggleButtonGroup
              size="small"
              sx={{
                ...tbgSxProps,
                mr: 2,
              }}
              exclusive
              value={timeRange}
              onChange={(e:any, v:TimeRangeType) => setTimeRange(v)}
            >
              <ToggleButton value={TimeRangeType.Hour}>
                <Typography variant="subtitle2" color="textSecondary">H</Typography>
              </ToggleButton>
              <ToggleButton value={TimeRangeType.Day}>
                <Typography variant="subtitle2" color="textSecondary">D</Typography>
              </ToggleButton>
              <ToggleButton value={TimeRangeType.Week}>
                <Typography variant="subtitle2" color="textSecondary">W</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          }
        />
        <CardContent>
          <ChartLegends series={finalData.series} onClick={handleChange} />
          <Chart
            height="350"
            type="line"
            {...chart}
          />
        </CardContent>
      </Card>
    </Box>
  )
}

export default MaxQueryTimeChart;
